import React, { useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import proptypes from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import {
  Alert,
  DrawerHeader,
  DrawerBody,
  Drawer,
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import { dataModel } from './dataModel';
import { useSwapProducts } from './useSwapProducts';
import { ElligibleItems } from '../elligible-items/ElligibleItems';
import { CurrentItem } from '../current-item/CurrentItem';
import { getDimensions } from '../elligible-items/product-pod/dimensions';
import { SwapContextProvider } from '../SwapContext';
import { DRAWER_FLOWS, OOS_RECOMMENDATIONS_LIMIT, getFlowContent } from '../../../constants/drawerFlows';

const { string, bool, func, shape, oneOf, arrayOf } = proptypes;

const loadingSkeleton = (
  <div data-testid="swap-drawer__loading">
    {[...Array(OOS_RECOMMENDATIONS_LIMIT).keys()].map((key) => (
      <Skeleton key={key} orientation="horizontal" disablePadding="x" grow>
        <SkeletonContent disablePadding="x" initialSize="1/4">
          <SkeletonBlock aspect="square" />
        </SkeletonContent>
        <SkeletonContent disablePadding="x" grow>
          <SkeletonLine variant="multi" numberOfLines={4} />
        </SkeletonContent>
      </Skeleton>
    ))}
  </div>
);

const SwapDrawer = ({
  itemId,
  promoId,
  openSwapDrawer,
  onClose,
  fetch,
  onSwap,
  hideCurrentSelectedItem,
  currentItemSelectable,
  selectionButtonContent,
  openInQuickViewDescription,
  openDetailsInNewTab,
  hideQuickViewMask,
  itemIndex,
  drawerRootProps,
  openCurrentItemInQuickView,
  filterResponse,
  flow,
  navParam,
  categoryHierarchy,
  zipCode
}) => {
  const { storeId, storeZip } = useStore();
  const { channel } = useContext(ExperienceContext) || {};
  const isOutOfStockFlow = flow === DRAWER_FLOWS.OOS_RECOMMENDATIONS;
  const flowContent = getFlowContent(flow, { categoryHierarchy });
  const [currentItem, setCurrentItem] = useState({});
  const {
    hasResponse, products, loading, error, currentItemSpecificationGroup
  } = useSwapProducts({
    flow,
    itemId,
    storeId,
    zipCode: zipCode || storeZip,
    channel,
    promoId,
    filterResponse,
    navParam,
    fetch
  });

  useEffect(() => {
    if (!loading && openSwapDrawer && !isOutOfStockFlow) {
      if (products) {
        const output = {
          position: itemIndex
        };
        window.LIFE_CYCLE_EVENT_BUS.trigger('swap-item.success', output);

      } else {
        const message = error?.message || 'n/a';
        const code = error?.graphQLErrors?.[0]?.extensions?.id || 500;
        window.LIFE_CYCLE_EVENT_BUS.trigger('swap-item.error', { message, code });
      }
    }
  }, [products, loading, openSwapDrawer, error]);

  useEffect(() => {
    if (openSwapDrawer && isOutOfStockFlow) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('swap-item.oos-recommendations-overlay');
    }
  }, [openSwapDrawer, isOutOfStockFlow]);

  if (!hasResponse) {
    return null;
  }

  const showLoading = isOutOfStockFlow && loading;
  const currentItemDimensions = isOutOfStockFlow && flowContent.showDimensions
    ? getDimensions(currentItemSpecificationGroup)
    : null;

  const swappableProducts = products?.filter((product) => product.itemId !== itemId);
  const elligibleItems = (
    <ElligibleItems
      products={swappableProducts}
      anchorItem={itemId}
      openInQuickViewDescription={openInQuickViewDescription}
      openDetailsInNewTab={openDetailsInNewTab}
      hideQuickViewMask={hideQuickViewMask}
      hideKPF={flowContent.hideKPF}
      showDimensions={flowContent.showDimensions}
      zeroBasedPositions={Boolean(flowContent.impressionContainer)}
    />
  );

  return createPortal(
    <div data-component="SwapDrawer">
      <Drawer
        open={isOutOfStockFlow ? openSwapDrawer : openSwapDrawer && !loading && fetch}
        onClose={onClose}
        DrawerRootProps={drawerRootProps}
      >
        <div className="sui-bg-primary sui-shadow-md sui-mb-2 sui-sticky sui-top-0 sui-z-10">
          <DrawerHeader
            className="sui-h-12 sui-grid"
            title={(
              <div className="sui-p-2">
                <Typography
                  component="span"
                  variant="h4"
                  color="primary"
                  weight="bold"
                >{flowContent.drawerHeading || 'Make New Selection'}
                </Typography>
              </div>
            )}
            onClose={onClose}
          />
        </div>
        <SwapContextProvider value={{
          onSwap,
          currentItem,
          selectionButtonContent: selectionButtonContent || flowContent.selectionButtonContent,
          setCurrentItem,
          openSwapDrawer,
          onClose
        }}
        >
          <DrawerBody
            className="sui-px-4 sui-pt-2 sui-pb-4"
            name="swap-products"
          >
            {showLoading && loadingSkeleton}
            {!hideCurrentSelectedItem && !showLoading
              && (
                <CurrentItem
                  itemId={itemId}
                  storeId={storeId}
                  currentItemSelectable={currentItemSelectable}
                  openCurrentItemInQuickView={openCurrentItemInQuickView}
                  openDetailsInNewTab={openDetailsInNewTab}
                  hideQuickViewMask={hideQuickViewMask}
                  flow={flow}
                  categoryHierarchy={categoryHierarchy}
                  dimensions={currentItemDimensions}
                />
              )}
            <div className="sui-pt-6">
              <section>
                {products?.length > 0
                  ? (
                    <>
                      <Typography
                        height="tight"
                        variant="h5"
                      >{flowContent.differentItemHeading || `Select ${hideCurrentSelectedItem ? 'an Item' : 'a Different Item'}`}
                      </Typography>
                      {flowContent.impressionContainer ? (
                        <ImpressionProvider data={flowContent.impressionContainer}>
                          {elligibleItems}
                        </ImpressionProvider>
                      ) : elligibleItems}
                    </>
                  )
                  : !showLoading && (
                    <Alert status="error">
                      <Typography>{flowContent.emptyStateMessage || 'Sorry! No eligible items available to swap.'}</Typography>
                    </Alert>
                  )}
              </section>
            </div>
          </DrawerBody>
        </SwapContextProvider>
      </Drawer>
    </div>,
    document.body
  );
};

SwapDrawer.propTypes = {
  itemId: string.isRequired,
  promoId: string,
  openSwapDrawer: bool.isRequired,
  onClose: func.isRequired,
  fetch: bool.isRequired,
  onSwap: func.isRequired,
  hideCurrentSelectedItem: bool.isRequired,
  selectionButtonContent: string,
  currentItemSelectable: bool,
  openInQuickViewDescription: bool,
  openDetailsInNewTab: bool,
  hideQuickViewMask: bool,
  itemIndex: string,
  drawerRootProps: shape({}),
  openCurrentItemInQuickView: bool,
  filterResponse: bool,
  flow: oneOf(Object.values(DRAWER_FLOWS)),
  navParam: string,
  categoryHierarchy: arrayOf(string),
  zipCode: string
};

SwapDrawer.defaultProps = {
  promoId: null,
  selectionButtonContent: null,
  currentItemSelectable: false,
  openInQuickViewDescription: false,
  openDetailsInNewTab: false,
  hideQuickViewMask: false,
  itemIndex: 'n/a',
  drawerRootProps: {},
  openCurrentItemInQuickView: false,
  filterResponse: true,
  flow: DRAWER_FLOWS.DEFAULT,
  navParam: null,
  categoryHierarchy: null,
  zipCode: null
};

SwapDrawer.displayName = 'SwapDrawer';

SwapDrawer.dataModel = dataModel;

export { SwapDrawer };
